<script lang="ts">
  import L, { type Circle, type Marker } from 'leaflet'
  import { getContext, onDestroy, onMount, tick } from 'svelte'
  import { warn } from '#app/lib/loggers'
  import { onChange } from '#app/lib/svelte/svelte'
  import type { LatLng } from '#server/types/common'
  import type { Snippet } from 'svelte'

  let {
    latLng,
    standalone = false,
    markerType,
    metersRadius = 200,
    marker = $bindable(),
    children,
  }: {
    latLng: LatLng
    standalone?: boolean
    markerType?: 'circle'
    metersRadius?: number
    marker?: Marker | Circle
    children?: Snippet
  } = $props()

  let markerElement: HTMLElement = $state()

  const map = getContext('map')()
  const layer = getContext('layer')()
  const targetLayer = $derived(standalone ? map : layer)

  async function createMarker () {
    if (!markerElement) await tick()
    if (marker) return
    if (!latLng) {
      warn('can not create marker: missing latLng')
      return
    }
    if (markerType === 'circle') {
      // Use a large border ("weight") to make the marker easily visible even when very zoomed out
      marker = L.circle(latLng, { radius: metersRadius, weight: 10 }).addTo(targetLayer)
    } else {
      const icon = L.divIcon({
        html: markerElement,
        // Let the children elements determine the size
        iconSize: [ 0, 0 ],
      })
      marker = L.marker(latLng, { icon }).addTo(targetLayer)
    }
  }

  function updateMarkerLatLng () {
    if (marker) {
      marker.setLatLng(latLng)
    } else {
      createMarker()
    }
  }

  function destroyMarker () {
    if (marker) {
      // @ts-expect-error
      marker.removeFrom(targetLayer)
      marker.remove()
      marker = null
    }
  }

  onMount(createMarker)
  onDestroy(destroyMarker)
  $effect(() => {
    onChange(latLng, updateMarkerLatLng)
  })
</script>

<div bind:this={markerElement}>
  {#if marker}
    {@render children?.()}
  {/if}
</div>
